// // src/views/find/FindView.tsx
/* eslint-disable max-len */
import React, {
  FC, useEffect, useState, useRef,
} from 'react';
import './FindView.less';
import './slice/slice';
import { Button } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { BASE_API_URL } from '../../services/ApiService';
import { ReactComponent as Sound } from '../../assets/icons/games/find/volume-high.svg';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../constants/Roles';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import { getListWordId } from '../../utils/gameWords';
import WordCounter from '../../components/WordCounter';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

const generateEightPictures = (currentWord: any, allPics: any[]) => {
  if (!currentWord) return [];
  const otherWords = allPics.filter((word) => word.id !== currentWord.id);
  const uniqueWords = [...new Map(
    otherWords.map((word) => [word.term, word]),
  ).values()];
  const differentTerms = uniqueWords.filter(
    (word) => word.term !== currentWord.term,
  );
  const shuffled = [...differentTerms].sort(() => 0.5 - Math.random());
  const sevenWords = shuffled.slice(0, 7);
  const allEight = [...sevenWords, currentWord];

  return allEight.sort(() => 0.5 - Math.random());
};

export const FindView: FC = () => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList;
  const [statePics, setStatePics] = useState(() => [...words].sort(() => 0.5 - Math.random()));
  const [wordSpokenIndex, setWordSpokenIndex] = useState<number>(0);
  const [mistakeStyle, setMistakeStyle] = useState<string | boolean>(false);
  const [correctStyle, setCorrectStyle] = useState<string | boolean>(false);
  const [listEndInfo, setListEndInfo] = useState(false);

  const englishAudio = useAudioPlayer();

  useActivityTracking(ActivityGame.FIND);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  // Przełącznik PL decyduje o polskim znaczeniu po złej i po dobrej odpowiedzi. Szukane słowo
  // (autoodtwarzanie, SOUND) brzmi tylko po angielsku, żeby nie podpowiadać odpowiedzi.
  // Nowy klucz, bo pod starym przeglądarki zapamiętały wyłączony PL, a teraz na start ma być włączony.
  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio, // Nowa funkcja z hooka
  } = usePolishAudio({ storageKey: 'find_playPolish_v3', defaultOn: true });

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // ✅ Ref do śledzenia czy to nowe słowo
  const lastWordSpokenIndexRef = useRef<number>(-1);

  // ✅ State dla obrazków
  const [currentPictures, setCurrentPictures] = useState<any[]>([]);

  // Helper do dodawania timeout z auto-cleanup
  const addTimeout = (callback: () => void, delay: number) => {
    const timeoutId = setTimeout(callback, delay);
    timeoutsRef.current.push(timeoutId);

    return timeoutId;
  };

  // Cleanup wszystkich timeoutów
  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  // Funkcja zatrzymująca wszystkie audio
  const stopAllAudio = () => {
    englishAudio.stop();
    stopPolishAudio();
  };

  const numberOfWords = statePics.length;
  const index = wordSpokenIndex + 1;

  // Funkcja dodająca słowo do powtórki
  const addWordToRepeat = () => {
    const currentWord = statePics[wordSpokenIndex];
    const wordTerm = currentWord.term;

    const remainingWords = statePics.slice(wordSpokenIndex + 1);
    const alreadyHasRepeat = remainingWords.some((word) => word.term === wordTerm);

    if (alreadyHasRepeat) {
      return;
    }

    let needsSeparator = false;
    if (wordSpokenIndex < statePics.length - 1) {
      const nextWordTerm = statePics[wordSpokenIndex + 1].term;
      needsSeparator = (nextWordTerm === wordTerm);
    } else {
      needsSeparator = true;
    }

    const wordsToAdd = [];

    if (needsSeparator) {
      let randomWordObj;
      let attempts = 0;
      const maxAttempts = 50;

      do {
        let randomIndex;

        if (attempts < 30) {
          const preferredRange = Math.ceil(words.length * 0.7);
          randomIndex = Math.floor(Math.random() * preferredRange);
        } else {
          randomIndex = Math.floor(Math.random() * words.length);
        }

        randomWordObj = words[randomIndex];
        attempts += 1;
      } while (
        attempts < maxAttempts
        && randomWordObj.term === wordTerm
      );

      const randomCopy = { ...randomWordObj };
      randomCopy.index = statePics.length + wordsToAdd.length;
      randomCopy.id = randomCopy.index.toString();
      randomCopy.listWordId = getListWordId(randomWordObj);

      wordsToAdd.push(randomCopy);
    }

    const wordToRepeat = { ...currentWord };
    wordToRepeat.index = statePics.length + wordsToAdd.length;
    wordToRepeat.id = wordToRepeat.index.toString();
    wordToRepeat.listWordId = getListWordId(currentWord);

    wordsToAdd.push(wordToRepeat);

    setStatePics([
      ...statePics,
      ...wordsToAdd,
    ]);
  };

  // Odtworzenie szukanego słowa po angielsku – bez polskiego, żeby nie podpowiadać odpowiedzi
  const playSearchedWord = async () => {
    const searchedWord = statePics[wordSpokenIndex];
    if (!searchedWord) return;

    stopAllAudio();

    // ZMIANA: Używamy cleanTextForTTS dla angielskiego audio
    const cleanTerm = cleanTextForTTS(searchedWord.term);
    if (!cleanTerm) return;

    const url = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;
    await englishAudio.play(url);
  };

  // Obsługa błędnego kliknięcia – kliknięte słowo po angielsku, a z włączonym PL po nim jego polskie znaczenie
  const handleWrongClick = (clickedWord: any) => {
    setCorrectStyle(false);
    setMistakeStyle(clickedWord.id);
    addWordToRepeat();

    stopAllAudio();

    // ZMIANA: Używamy cleanTextForTTS dla angielskiego audio
    const cleanTerm = cleanTextForTTS(clickedWord.term);
    if (!cleanTerm) return;

    const englishUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;

    englishAudio.play(englishUrl, () => {
      addTimeout(() => {
        playPolishAudio(clickedWord.definition);
      }, 200);
    });
  };

  // Obsługa poprawnego kliknięcia (nie ostatnie słowo) – z włączonym PL polskie znaczenie, potem następne słowo
  const handleCorrectClick = (id: string) => {
    setMistakeStyle(false);
    setCorrectStyle(id);
    stopAllAudio();

    addTimeout(() => {
      playPolishAudio(statePics[wordSpokenIndex].definition, () => {
        addTimeout(() => {
          setWordSpokenIndex((prev: number) => prev + 1);
          setCorrectStyle(false);
        }, 300);
      });
    }, 600);
  };

  // Obsługa poprawnego kliknięcia (ostatnie słowo) – z włączonym PL polskie znaczenie, potem ekran końca gry
  const handleCorrectLastClick = (id: string) => {
    setCorrectStyle(id);
    stopAllAudio();

    addTimeout(() => {
      playPolishAudio(statePics[wordSpokenIndex].definition, () => {
        addTimeout(() => {
          setListEndInfo(true);
        }, 300);
      });
    }, 600);
  };

  // Główna funkcja kliknięcia w obrazek
  const handlePictureClick = (id: string, term: string) => {
    // Po dobrej odpowiedzi lektor czyta słowo – do pokazania następnego ignorujemy kliknięcia
    if (correctStyle !== false) return;

    // Kasuje czekające opóźnienia poprzedniego kliknięcia (polskie znaczenie po złej odpowiedzi),
    // żeby nie zabrzmiały razem z dźwiękami tego
    clearAllTimeouts();

    const isCorrect = statePics[wordSpokenIndex].term === term;
    const isLastWord = index === numberOfWords;

    recordAnswer(isCorrect, statePics[wordSpokenIndex].term);

    if (isCorrect && !isLastWord) {
      handleCorrectClick(id);
    } else if (isCorrect && isLastWord) {
      handleCorrectLastClick(id);
    } else {
      const clickedWord = currentPictures.find((word) => word.id === id);
      if (clickedWord) {
        handleWrongClick(clickedWord);
      }
    }
  };

  // Przycisk SOUND – po dobrej odpowiedzi nie przerywa lektora, bo gra nie poszłaby dalej.
  // Blokada tylko tu, nie w playSearchedWord: autoodtwarzanie nowego słowa woła playSearchedWord
  // z domknięcia, w którym correctStyle jest jeszcze ustawione.
  // Kasuje też czekające polskie znaczenie po złej odpowiedzi, żeby nie weszło na angielskie słowo.
  const handleSoundClick = () => {
    if (correctStyle !== false) return;
    clearAllTimeouts();
    playSearchedWord();
  };

  const goToNext = useGoToRoute(Path.PICK);

  // Ponowna gra - nowy shuffle
  const repeatGame = () => {
    stopAllAudio();
    clearAllTimeouts();

    addTimeout(() => {
      setStatePics([...words].sort(() => 0.5 - Math.random()));
      setWordSpokenIndex(0);
      setCorrectStyle(false);
      setMistakeStyle(false);
      setListEndInfo(false);
      lastWordSpokenIndexRef.current = -1;
    }, 500);
  };

  // Cleanup przy unmount
  useEffect(() => () => {
    stopAllAudio();
    clearAllTimeouts();
  }, []);

  // ✅ Effect do aktualizacji obrazków - smart update (BEZ useMemo!)
  useEffect(() => {
    const currentWord = statePics[wordSpokenIndex];
    if (!currentWord) return;

    if (wordSpokenIndex !== lastWordSpokenIndexRef.current) {
      // Nowe słowo - wygeneruj obrazki od nowa (Z shufflem)
      const newPictures = generateEightPictures(currentWord, statePics);
      setCurrentPictures(newPictures);
      lastWordSpokenIndexRef.current = wordSpokenIndex;
    } else {
      // To samo słowo, ale zmienił się statePics (edycja) - zaktualizuj dane BEZ shuffla
      setCurrentPictures((prev) => prev.map((word) => {
        const updated = statePics.find((w) => w.id === word.id);

        return updated || word;
      }));
    }
  }, [wordSpokenIndex, statePics]);

  // Effect przy zmianie słowa - autoplay
  useEffect(() => {
    if (!statePics[wordSpokenIndex]) {
      return () => {};
    }

    clearAllTimeouts();

    const timeoutId = setTimeout(() => playSearchedWord(), 700);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [wordSpokenIndex]);

  const currentWord = statePics[wordSpokenIndex];

  return (
    <>
      <div className="find-main">
        <FixWidthContent>
          {!listEndInfo && (
          <WordCounter
            current={wordSpokenIndex + 1}
            total={statePics.length}
          />
          )}
          {!listEndInfo && (
            <>
              <section>
                <div className="pictures_container">
                  {currentPictures.map((word: any) => (
                    <div key={`picture-${word.id}-${word.term}`} className="picture_cell">
                      <div
                        className={`pictures ${word.id === mistakeStyle ? 'mistake' : ''} ${word.id === correctStyle ? 'correct' : ''}`}
                        id={word.id}
                        onClick={() => handlePictureClick(word.id, word.term)}
                        // Klawiatura jak przy przycisku: Enter albo spacja, przytrzymany klawisz raz. Inaczej każdy klawisz
                        // na obrazku z fokusem (np. Tab po złym kliknięciu) byłby kolejną odpowiedzią
                        onKeyDown={(event) => {
                          if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) {
                            handlePictureClick(word.id, word.term);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={t('games:labels.markRightWord')}
                      >
                        {word.image && (
                          <img
                            src={word.image}
                            alt={word.term}
                          />
                        )}
                      </div>
                      {word.definition && (
                        <span className="picture_caption">{word.definition}</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <div className="sound_button_container">
                <Button id="sound_button" onClick={handleSoundClick}>
                  <Sound className="btnLabel" />
                  <span className="btnText">{t('games:buttons.sound')}</span>
                </Button>
              </div>
            </>
          )}

          {listEndInfo && (
            <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} />
          )}

          {!listEndInfo && <UnsplashCredits words={currentPictures} />}

          <RightSideButtons>
            <InfoButton content={<RulesOfTheGame />} />

            {canEditTerms && currentWord && (
              <EditTermButton
                wordId={getListWordId(currentWord)}
                currentTerm={currentWord.term}
                currentDefinition={currentWord.definition}
                listId={activeList?.id || ''}
                onTermUpdated={(newTerm, newDefinition) => {
                  const listWordId = getListWordId(currentWord);

                  setStatePics((prev) => prev.map((word) => (getListWordId(word) === listWordId
                    ? { ...word, term: newTerm, definition: newDefinition }
                    : word)));
                }}
              />
            )}

            <PolishAudioButton
              playPolish={playPolish}
              onToggle={togglePlayPolish}
            />

            {canEditTerms && activeList?.id && (
              <EditListButton listId={activeList.id} />
            )}

          </RightSideButtons>
        </FixWidthContent>
      </div>
    </>
  );
};

export default withTagManager(FindView);
